import React, {
  useCallback,
  useRef,
  useEffect,
  useMemo,
  useState,
} from 'react';

import { includes, isEmpty } from 'lodash';
import PropTypes from 'prop-types';
import * as Yup from 'yup';

import { Select, Input, Form, Autocomplete, Combobox } from '~/components/Form';
import { t } from '~/helpers';

import * as S from './styles';

export default function MTable({
  sending,
  items,
  ignore,
  label,
  onChangeItem,
  fields,
  initials,
  onField,
  isSchema,
  onModify,
  isIgnore,
  onCallback,
}) {
  const formRef = useRef(null);

  const [posto, setPosto] = useState(null);

  const array = useMemo(
    () =>
      items.filter(
        (item) =>
          !includes(
            ignore.map((ig) => ig.id),
            item.id
          )
      ),
    [items, ignore]
  );

  const ignoreOptions = useCallback((value) => {
    if (!isEmpty(isIgnore)) {
      const options = value.filter(
        (v) =>
          !includes(
            ignore.map((ig) => ig[isIgnore]),
            v.value
          )
      );

      if (!isEmpty(initials)) {
        const isPush = fields
          .find((f) => f.id === isIgnore)
          .options.find((o) => o.value === initials[isIgnore]);

        options.push(isPush);
      }

      return options;
    }

    return value;
  }, []);

  const renderForm = useMemo(() => {
    if (isEmpty(fields)) {
      return <Select name="item" label={label} options={items} />;
    }

    return fields.map((field) => {
      switch (field.type) {
        case 'select':
          return (
            <Select
              name={field.id}
              label={field.label}
              disabled={!isEmpty(initials) && field.disabled}
              options={ignoreOptions(field.options)}
            />
          );
        case 'autocomplete':
          return (
            <Autocomplete
              name={field.id}
              label={field.option.label}
              url={field.option.url}
              unique={field.option.unique}
              title={field.label}
              disabled={
                !isEmpty(initials) && field.disabledOnUpdate
                  ? true
                  : !!field.disabled
              }
              excluded={ignore.map((m) => m[field.option.unique])}
            />
          );
        case 'text':
        case 'number':
        default:
          return (
            <Input
              name={field.id}
              label={field.label}
              type={field.type}
              disabled={
                !isEmpty(initials) && field.disabledOnUpdate
                  ? true
                  : !!field.disabled
              }
            />
          );
      }
    });
  }, [fields, label, array, ignore, isIgnore, initials, items]);

  const handleSubmit = useCallback(
    async (data, { reset }) => {
      try {
        const schema = Yup.object().shape(
          isEmpty(fields)
            ? {
                item: Yup.string().required(
                  t('s.geral.validacao.campoobrigatorio')
                ),
              }
            : isSchema
        );

        await schema.validate(data, {
          abortEarly: false,
        });

        if (isEmpty(fields) || isEmpty(initials)) {
          const isData = isEmpty(fields)
            ? items.find((item) => item.id === data.item)
            : onField(data);

          onChangeItem(isData);
          onCallback({
            type: 'create',
            data: isData,
          });
        } else {
          onModify(onField(data));
          onCallback({
            type: 'modify',
            data: onField(data),
          });
        }
        reset();
      } catch (err) {
        const validationErrors = {};

        if (err instanceof Yup.ValidationError) {
          err.inner.forEach((error) => {
            validationErrors[error.path] = error.message.replace(
              `${error.path} `,
              ''
            );
          });

          formRef.current.setErrors(validationErrors);
        }
      }
    },
    [fields]
  );

  useEffect(() => {
    if (sending) formRef.current.submitForm();
  }, [sending]);

  return (
    <S.Wrapper>
      <Form ref={formRef} onSubmit={handleSubmit} initialData={initials}>
        <S.WrapperForm>
          <Autocomplete
            name="idposto"
            label="codigonomeposto"
            url="postoscomboios"
            unique="idposto"
            title={t('s.menu.configuracao.posto')}
            hiddens={[{ name: 'codigonomeposto' }]}
            getValue={(value) => {
              if (value.idposto) {
                setPosto(value.idposto);
              } else {
                setPosto(null);
              }
            }}
          />

          {posto && (
            <>
              <Combobox
                name="idbico"
                label="bicoprodutobico"
                url="bicos"
                unique="idbico"
                title={t('s.menu.configuracao.bico')}
                hiddens={[{ name: 'idterminal' }, { name: 'idproduto' }]}
                params={{
                  wherefield: ['idposto'],
                  valuewherefield: [posto],
                  typewherefield: ['EQUALS'],
                  fieldlist: 'idbico, idterminal, idproduto, bicoprodutobico',
                }}
              />

              <Input
                name="vazaomaxima"
                type="number"
                label={t('s.operacao.abastecimento.vazaomaximamovimentacao')}
              />
            </>
          )}
        </S.WrapperForm>
      </Form>
    </S.Wrapper>
  );
}

MTable.propTypes = {
  sending: PropTypes.bool.isRequired,
  items: PropTypes.arrayOf(PropTypes.any).isRequired,
  label: PropTypes.string.isRequired,
  onChangeItem: PropTypes.func.isRequired,
  ignore: PropTypes.arrayOf(PropTypes.any).isRequired,
  fields: PropTypes.arrayOf(PropTypes.any).isRequired,
  initials: PropTypes.objectOf(PropTypes.any).isRequired,
  onField: PropTypes.func.isRequired,
  isSchema: PropTypes.objectOf(PropTypes.any).isRequired,
  onModify: PropTypes.func.isRequired,
  isIgnore: PropTypes.string.isRequired,
  onCallback: PropTypes.func.isRequired,
};
